import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlContainer, NgForm, NgModel } from "@angular/forms";

import { CommonService } from "../common/common.service";
import { EventBusService, EventsEnum } from "../common/event-bus.service";
import { LanguageService } from "../common/language.service";
import { TransactionTypeEnum } from "../domain/enums";
import { IDonorTransactionBindingModel } from "../domain/types";
import {
  ApplePayDonationLabel,
  CardField,
  CardFieldOrder,
  CardFieldValue,
  DirectPaymentMethod,
  DirectPaymentMethodValue,
  isPaymentCancellation,
  PaymentChoice,
  PaymentChoiceValue,
  PayPalButtonContainer,
} from "../domain/payment.constants";
import { DataService } from "../services/data.service";
import {
  BankAccountDetails,
  BankAccountTokenizePayload,
  EcheckSdkService,
} from "../services/echeck-sdk.service";
import {
  CardFieldStates,
  PaymentAvailability,
  PaymentSdkService,
  PaymentTokenPayload,
} from "../services/payment-sdk.service";
import { CmnIconName } from "../shared/components/cmn-icon/cmn-icon.component";

interface CardFieldTracking {
  visited: boolean;
  empty: boolean;
  valid: boolean;
}

interface PaymentChoiceOption {
  choice: PaymentChoiceValue;
  icon: CmnIconName;
  labelKey?: string;
  /** Wallet brand names stay in English and are never translated. */
  label?: string;
}

interface CardFieldDefinition {
  field: CardFieldValue;
  hostId: string;
  labelKey: string;
  full: boolean;
}

const MOBILE_BREAKPOINT = 767;

const EMPTY_AVAILABILITY: PaymentAvailability = {
  card: false,
  paypal: false,
  venmo: false,
  applePay: false,
};

@Component({
  selector: "app-payment-details",
  templateUrl: "./payment-details.component.html",
  styleUrls: ["./payment-details.component.scss"],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentDetailsComponent implements AfterViewInit, OnDestroy {
  private _dataService = inject(DataService);
  private _common = inject(CommonService);
  private _languageService = inject(LanguageService);
  private _eventBus = inject(EventBusService);
  private _paymentSdk = inject(PaymentSdkService);
  private _echeckSdk = inject(EcheckSdkService);
  private _zone = inject(NgZone);
  private _destroyRef = inject(DestroyRef);

  transaction = input.required<IDonorTransactionBindingModel>();
  form = input<NgForm>();

  bankname = viewChild<NgModel>("bankname");
  routingnumber = viewChild<NgModel>("routingnumber");
  banknumber = viewChild<NgModel>("banknumber");

  readonly PaymentChoice = PaymentChoice;
  readonly CardField = CardField;
  readonly CmnIconName = CmnIconName;

  readonly cardFieldDefinitions: CardFieldDefinition[] = [
    {
      field: CardField.CardholderName,
      hostId: "cardholder-name",
      labelKey: 'NAME_ON_CARD',
      full: true,
    },
    {
      field: CardField.Number,
      hostId: "card-number",
      labelKey: 'CREDIT_CARD_NO',
      full: true,
    },
    {
      field: CardField.ExpirationDate,
      hostId: "expiration-date",
      labelKey: 'EXPIRATION_DATE',
      full: false,
    },
    {
      field: CardField.Cvv,
      hostId: "cvv",
      labelKey: 'PAYMENT_METHOD_CVV',
      full: false,
    },
    {
      field: CardField.PostalCode,
      hostId: "postal-code",
      labelKey: 'POSTAL_CODE',
      full: false,
    },
  ];

  selected = signal<PaymentChoiceValue | null>(null);
  availability = signal<PaymentAvailability>(EMPTY_AVAILABILITY);
  isAuthorizing = signal(false);
  authorized = signal<Partial<Record<DirectPaymentMethodValue, boolean>>>({});
  venmoUserName = signal("");
  accountNameChanged = signal(false);
  cardFields = signal<Record<CardFieldValue, CardFieldTracking>>(
    this._emptyCardFieldState()
  );

  readonly choices = computed(() => {
    const availability = this.availability();
    const options: PaymentChoiceOption[] = [
      {
        choice: PaymentChoice.Card,
        icon: CmnIconName.CreditCard,
        labelKey: "CREDIT_CARD_PAYMENT_TYPE",
      },
    ];

    if (availability.paypal) {
      options.push({
        choice: PaymentChoice.PayPal,
        icon: CmnIconName.PayPal,
        label: "PayPal",
      });
    }

    if (availability.venmo) {
      options.push({
        choice: PaymentChoice.Venmo,
        icon: CmnIconName.Mobile,
        label: "Venmo",
      });
    }

    if (availability.applePay) {
      options.push({
        choice: PaymentChoice.ApplePay,
        icon: CmnIconName.Apple,
        label: "Apple Pay",
      });
    }

    options.push({
      choice: PaymentChoice.Echeck,
      icon: CmnIconName.Money,
      labelKey: "ECHECK_PAYMENT_TYPE",
    });

    return options;
  });

  readonly isEcheckSelected = computed(
    () => this.selected() === PaymentChoice.Echeck
  );

  readonly selectedDirectMethod = computed((): DirectPaymentMethodValue | null => {
    const selected = this.selected();
    return this.isEcheckSelected() || !selected
      ? null
      : (selected as DirectPaymentMethodValue);
  });

  readonly transactionType = computed((): TransactionTypeEnum | null => {
    switch (this.selected()) {
      case PaymentChoice.Card:
        return TransactionTypeEnum.CreditCard;
      case PaymentChoice.PayPal:
        return TransactionTypeEnum.PayPal;
      case PaymentChoice.Venmo:
        return TransactionTypeEnum.Venmo;
      case PaymentChoice.ApplePay:
        return TransactionTypeEnum.ApplePay;
      case PaymentChoice.Echeck:
        return TransactionTypeEnum.ECheck;
      default:
        return null;
    }
  });

  readonly hasIncompleteCardFields = computed(() => {
    const cardFields = this.cardFields();
    return CardFieldOrder.some((field) => {
      const state = cardFields[field];
      return !state || state.empty || !state.valid;
    });
  });

  private readonly bankNameNotFoundKey = "BANK_NAME_NOT_FOUND";
  private _payPalButtonRendered = false;

  ngAfterViewInit(): void {
    this._eventBus
      .on(EventsEnum.LanguageChanged)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) => {
        this._paymentSdk.updatePaymentLanguage(
          event.data || this._languageService.language
        );
      });

    this._initializePaymentMethods();
  }

  ngOnDestroy(): void {
    this._paymentSdk.onCardFieldChange(null);
    this._paymentSdk.teardown();
    this._echeckSdk.teardown();
  }

  /**
   * eCheck validity lives on NgModel, which is not a signal. Keep this a getter
   * so donate still sees current form errors on each change-detection pass.
   */
  get hasErrors(): boolean {
    if (this.selected() === PaymentChoice.Card) {
      return this.hasIncompleteCardFields();
    }

    if (!this.isEcheckSelected()) {
      return false;
    }

    return [this.bankname(), this.routingnumber(), this.banknumber()].some(
      (control) => control && control.invalid
    );
  }

  select(choice: PaymentChoiceValue): void {
    if (this.selected() === choice) {
      return;
    }

    this._clearMethodErrors();
    this.selected.set(choice);

    if (choice === PaymentChoice.Echeck) {
      this._echeckSdk.initialize().catch((error: unknown) => {
        console.error(error);
          this._common.toast.error(
            this._languageService.translate("PAYMENT_METHOD_ERROR"),
            this._languageService.translate("PAYMENT_METHOD_HEADING")
          );
      });
    }

    if (choice === PaymentChoice.PayPal) {
      this._renderPayPalButton();
    }

    this._focusSelectedPanel(choice);
  }

  panelId(choice: PaymentChoiceValue): string {
    return `${this._choiceSlug(choice)}-payment-panel`;
  }

  choiceId(choice: PaymentChoiceValue): string {
    return `${this._choiceSlug(choice)}-payment-choice`;
  }

  showCardFieldError(field: CardFieldValue): boolean {
    const state = this.cardFields()[field];
    return state.visited && state.empty;
  }

  cardFieldErrorId(field: CardFieldValue): string {
    return `${field}-error`;
  }

  async authorizeVenmo(): Promise<void> {
    await this._authorizeWallet(DirectPaymentMethod.Venmo, async () => {
      const payload = await this._paymentSdk.authorizeVenmo();
      this.venmoUserName.set(payload?.details?.username || "");
      this._focusSelectedPanel(PaymentChoice.Venmo);
    });
  }

  async authorizeApplePay(): Promise<void> {
    await this._authorizeWallet(DirectPaymentMethod.ApplePay, () =>
      this._paymentSdk.authorizeApplePay(
        this.transaction()?.donationAmount,
        ApplePayDonationLabel
      )
    );
  }

  async preparePayment(amount: number): Promise<PaymentTokenPayload> {
    const method = this.selectedDirectMethod();
    if (!method) {
      throw new Error("No payment method is selected.");
    }

    if (method === DirectPaymentMethod.Card) {
      this.markCardFieldsVisited();
      if (this.hasIncompleteCardFields()) {
        throw new Error("Card fields are incomplete.");
      }
    }

    try {
      return await this._paymentSdk.preparePayment(method, amount);
    } catch (error) {
      if (method === DirectPaymentMethod.Card) {
        this.markCardFieldsVisited();
      } else {
        this._setAuthorized(method, false);
      }
      throw error;
    }
  }

  getDeviceData(): string {
    return this._paymentSdk.getDeviceData();
  }

  markCardFieldsVisited(): void {
    const values: CardFieldStates = this._paymentSdk.currentCardFieldValues();

    this.cardFields.update((cardFields) => {
      const next = { ...cardFields };
      CardFieldOrder.forEach((field) => {
        const current = values[field];
        next[field] = {
          visited: true,
          empty: current ? current.empty : cardFields[field].empty,
          valid: current ? current.valid : cardFields[field].valid,
        };
      });
      return next;
    });
  }

  markEcheckFieldsTouched(): void {
    [this.bankname(), this.routingnumber(), this.banknumber()].forEach(
      (model) => {
        if (model?.invalid) {
          model.control?.markAsTouched();
        }
      }
    );
  }

  tokenizeBankAccount(
    details: BankAccountDetails,
    mandateText: string
  ): Promise<BankAccountTokenizePayload> {
    return this._echeckSdk.tokenizeBankAccount(details, mandateText);
  }

  getBankName(): void {
    const abaNumber = this.transaction().abaNumber;
    if (abaNumber === undefined || abaNumber.length < 9) {
      return;
    }

    this._dataService.Donor.getBankFromRouting(abaNumber).subscribe({
      next: (result) => {
        this.transaction().bankName = result as string;
      },
      error: () => {
        const errorMessage = this._languageService.translate(
          this.bankNameNotFoundKey
        );
        this._common.toast.info(errorMessage, "", {
          timeOut: 10000,
          progressBar: true,
        });
      },
    });
  }

  showError(control: NgModel): boolean {
    return !!(control && (control.touched || control.dirty));
  }

  private async _initializePaymentMethods(): Promise<void> {
    this.cardFields.set(this._emptyCardFieldState());
    this._toggleSpinner(true);

    this._paymentSdk.onCardFieldChange((field, change) => {
      this.cardFields.update((cardFields) => {
        const state = cardFields[field];
        if (!state) {
          return cardFields;
        }

        return {
          ...cardFields,
          [field]: {
            visited: state.visited || !!change.visited,
            empty: change.empty,
            valid: change.valid,
          },
        };
      });
    });

    try {
      const availability = await this._paymentSdk.initializePaymentMethods(
        undefined,
        this._languageService.language
      );

      this.availability.set(availability);
      this._setAuthorized(
        DirectPaymentMethod.Venmo,
        this._paymentSdk.hasAuthorizedPayment(DirectPaymentMethod.Venmo)
      );
    } catch (error) {
      console.error(error);
      await this._paymentSdk.teardown();

        this._common.toast.error(
          this._languageService.translate("PAYMENT_METHOD_ERROR"),
          this._languageService.translate("PAYMENT_METHOD_HEADING")
        );
    } finally {
      this._toggleSpinner(false);
    }
  }

  private _renderPayPalButton(): void {
    if (this._payPalButtonRendered || !this.availability().paypal) {
      return;
    }

    this._payPalButtonRendered = true;

    // Let the panel become visible so PayPal can measure its container.
    setTimeout(() => {
      this._paymentSdk
        .renderPayPalButton(
          PayPalButtonContainer,
          () => this._setAuthorized(DirectPaymentMethod.PayPal, true),
          () => this.isAuthorizing.set(false),
          (error) => this._handleWalletError(error)
        )
        .catch((error: unknown) => {
          this._payPalButtonRendered = false;
          this._handleWalletError(error);
        });
    });
  }

  private async _authorizeWallet(
    method: DirectPaymentMethodValue,
    authorize: () => Promise<unknown>
  ): Promise<void> {
    if (this.isAuthorizing()) {
      return;
    }

    this.isAuthorizing.set(true);

    try {
      await authorize();
      this._setAuthorized(method, true);
    } catch (error) {
      this._handleWalletError(error);
    } finally {
      this.isAuthorizing.set(false);
    }
  }

  private _handleWalletError(error: unknown): void {
    if (isPaymentCancellation(error)) {
      return;
    }

    console.error(error);
      this._common.toast.error(
        this._languageService.translate("PAYMENT_METHOD_VERIFY"),
        this._languageService.translate("PAYMENT_METHOD_HEADING")
      );
  }

  private _clearMethodErrors(): void {
    const values = this._paymentSdk.currentCardFieldValues();
    const next = this._emptyCardFieldState();
    CardFieldOrder.forEach((field) => {
      const current = values[field];
      if (current) {
        next[field] = {
          visited: false,
          empty: current.empty,
          valid: current.valid,
        };
      }
    });
    this.cardFields.set(next);

    [this.bankname(), this.routingnumber(), this.banknumber()].forEach(
      (model) => {
        const formControl = model?.control;
        if (!formControl) {
          return;
        }
        formControl.markAsUntouched();
        formControl.markAsPristine();
      }
    );
  }

  private _focusSelectedPanel(choice: PaymentChoiceValue): void {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      return;
    }

    setTimeout(() => {
      const panel = document.getElementById(this.panelId(choice));
      if (!panel) {
        return;
      }

      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      panel.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
      panel.focus({ preventScroll: true });
    });
  }

  private _choiceSlug(choice: PaymentChoiceValue): string {
    return choice === PaymentChoice.ApplePay ? "apple-pay" : choice;
  }

  private _toggleSpinner(show: boolean): void {
    this._eventBus.emit({ name: EventsEnum.SpinnerToggle, data: show });
  }

  private _setAuthorized(
    method: DirectPaymentMethodValue,
    value: boolean
  ): void {
    this.authorized.update((current) => ({ ...current, [method]: value }));
  }

  private _emptyCardFieldState(): Record<CardFieldValue, CardFieldTracking> {
    return CardFieldOrder.reduce(
      (state, field) => {
        state[field] = { visited: false, empty: true, valid: false };
        return state;
      },
      {} as Record<CardFieldValue, CardFieldTracking>
    );
  }
}
